import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/Layout";
import { useAuth, API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { ProfileBadge, ProfileBadgeStrip, PROFILE_TYPE_OPTIONS } from "../components/ProfileBadge";
import {
  Search, MapPin, Award, Code, Monitor, Building2, Download,
  Filter, X, ChevronLeft, ChevronRight, Users, BarChart3, Rocket
} from "lucide-react";

// ============================================
// Working With Me modal (shown from Talent DB cards)
// ============================================
const WORKING_STYLE_CATEGORY_ORDER = [
  "Communication", "Focus & Environment", "Feedback & Support",
  "Sensory Needs", "Collaboration", "Strengths"
];

const WorkingWithMeModal = ({ profileId, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !profileId) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [qRes, rRes] = await Promise.all([
          fetch(`${API}/working-style/questions`, { credentials: "include" }),
          fetch(`${API}/working-style/response/${profileId}`, { credentials: "include" }),
        ]);
        if (!rRes.ok) {
          setError(rRes.status === 403 ? "This profile is private." : "No Working Style profile yet.");
          setLoading(false);
          return;
        }
        const qData = qRes.ok ? await qRes.json() : { questions: [] };
        const rData = await rRes.json();
        const map = {};
        (rData.answers || []).forEach((a) => { map[a.question_id] = a.answer; });
        setQuestions(qData.questions || []);
        setAnswers(map);
      } catch {
        setError("Failed to load");
      }
      setLoading(false);
    };
    load();
  }, [open, profileId]);

  const grouped = questions.reduce((acc, q) => {
    if (answers[q.question_id] === undefined) return acc;
    (acc[q.category] = acc[q.category] || []).push(q);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="working-with-me-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Rocket className="w-5 h-5 text-[#1E3A8A]" /> Working With Me
          </DialogTitle>
          <DialogDescription>How this candidate works best — shared by them.</DialogDescription>
        </DialogHeader>
        {loading && <div className="text-center py-8 text-slate-500">Loading…</div>}
        {error && <div className="text-center py-8 text-slate-500">{error}</div>}
        {!loading && !error && (
          <div className="space-y-5">
            {WORKING_STYLE_CATEGORY_ORDER.map((cat) => {
              const qs = grouped[cat];
              if (!qs || qs.length === 0) return null;
              return (
                <div key={cat} className="space-y-3">
                  <h4 className="text-sm font-heading font-bold text-[#1E3A8A] uppercase tracking-wide">{cat}</h4>
                  {qs.map((q) => {
                    const a = answers[q.question_id];
                    return (
                      <div key={q.question_id} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <p className="text-xs text-slate-500 mb-1">{q.text}</p>
                        <p className="text-sm font-medium text-slate-900 whitespace-pre-wrap">
                          {Array.isArray(a) ? a.join(" · ") : typeof a === "number" ? `${a} / 5` : String(a)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {Object.keys(grouped).length === 0 && (
              <div className="text-center py-6 text-slate-500">No answers shared.</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const TalentProfileCard = ({ profile, isAdmin }) => {
  const [wsOpen, setWsOpen] = useState(false);
  const location = [profile.city, profile.state, profile.country].filter(Boolean).join(", ");

  return (
    <Card className="card-surface card-hover overflow-hidden" data-testid={`talent-card-${profile.profile_id}`}>
      <ProfileBadgeStrip type={profile.profile_type} />
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-white font-semibold">
              {isAdmin && profile.name ? profile.name : <span className="italic opacity-60">Anonymous</span>}
            </h3>
            {isAdmin && profile.email && (
              <p className="text-slate-400 text-xs mt-0.5">{profile.email}</p>
            )}
          </div>
          <ProfileBadge type={profile.profile_type} compact />
        </div>
        {location && (
          <p className="text-slate-400 text-sm flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" />{location}
          </p>
        )}
        {profile.experience_years && (
          <p className="text-slate-500 text-sm">{profile.experience_years} yrs experience</p>
        )}
        {profile.skills?.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Skills</p>
            <div className="flex flex-wrap gap-1.5">
              {profile.skills.slice(0, 6).map((s, i) => (
                <span key={i} className="px-2 py-0.5 text-xs rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/20">
                  {s}
                </span>
              ))}
              {profile.skills.length > 6 && (
                <span className="px-2 py-0.5 text-xs rounded bg-slate-50 text-slate-400">
                  +{profile.skills.length - 6}
                </span>
              )}
            </div>
          </div>
        )}
        {profile.certifications?.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Certifications</p>
            <div className="flex flex-wrap gap-1.5">
              {profile.certifications.slice(0, 4).map((c, i) => (
                <span key={i} className="px-2 py-0.5 text-xs rounded bg-purple-500/15 text-purple-300 border border-purple-500/20">
                  {c}
                </span>
              ))}
              {profile.certifications.length > 4 && (
                <span className="px-2 py-0.5 text-xs rounded bg-slate-50 text-slate-400">
                  +{profile.certifications.length - 4}
                </span>
              )}
            </div>
          </div>
        )}
        {profile.software?.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Software</p>
            <div className="flex flex-wrap gap-1.5">
              {profile.software.slice(0, 4).map((sw, i) => (
                <span key={i} className="px-2 py-0.5 text-xs rounded bg-amber-500/15 text-amber-300 border border-amber-500/20">
                  {sw}
                </span>
              ))}
              {profile.software.length > 4 && (
                <span className="px-2 py-0.5 text-xs rounded bg-slate-50 text-slate-400">
                  +{profile.software.length - 4}
                </span>
              )}
            </div>
          </div>
        )}
        {(profile.working_style_shared || isAdmin) && profile.profile_id && (
          <button
            type="button"
            onClick={() => setWsOpen(true)}
            className="w-full mt-3 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border-2 border-[#FFD507]/30 bg-[#FFD507]/5 text-[#1E3A8A] hover:bg-[#FFD507]/10 transition-colors"
            data-testid={`view-working-style-${profile.profile_id}`}
          >
            <Rocket className="w-4 h-4" /> Working With Me
          </button>
        )}
      </CardContent>
      <WorkingWithMeModal profileId={profile.profile_id} open={wsOpen} onClose={setWsOpen} />
    </Card>
  );
};

const TalentDatabase = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [results, setResults] = useState({ profiles: [], total: 0, page: 1, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [skillFilter, setSkillFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [affiliationFilter, setAffiliationFilter] = useState("");
  const [profileTypeFilter, setProfileTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [matrixData, setMatrixData] = useState(null);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "18" });
      if (searchQuery) params.set("q", searchQuery);
      if (skillFilter) params.set("skills", skillFilter);
      if (locationFilter) params.set("location", locationFilter);
      if (affiliationFilter) params.set("affiliation", affiliationFilter);
      if (profileTypeFilter) params.set("profile_type", profileTypeFilter);

      const res = await fetch(`${API}/talent/search?${params}`, { credentials: "include" });
      if (res.ok) setResults(await res.json());
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, skillFilter, locationFilter, affiliationFilter, profileTypeFilter]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  useEffect(() => {
    fetch(`${API}/public/skills-matrix`)
      .then(r => r.json())
      .then(setMatrixData)
      .catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchProfiles();
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSkillFilter("");
    setLocationFilter("");
    setAffiliationFilter("");
    setProfileTypeFilter("");
    setPage(1);
  };

  const handleExport = async (format) => {
    try {
      const res = await fetch(`${API}/talent/export?format=${format}`, { credentials: "include" });
      if (res.ok) {
        if (format === "csv") {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `talent_export.${format}`;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          const data = await res.json();
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "talent_export.json";
          a.click();
          URL.revokeObjectURL(url);
        }
        toast.success(`Exported ${format.toUpperCase()} successfully`);
      } else {
        toast.error("Export failed — admin access required");
      }
    } catch { toast.error("Export error"); }
  };

  const topSkills = matrixData ? Object.keys(matrixData.skill_counts || {}).slice(0, 20) : [];

  return (
    <PageLayout
      title="Talent Database"
      subtitle={`Search and explore ${results.total} talent profiles.`}
    >
      <div className="space-y-6" data-testid="talent-database">
        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search skills, certifications, locations..."
                className="input-base pl-10"
                data-testid="talent-search-input"
              />
            </div>
            <Button type="submit" className="btn-primary" data-testid="talent-search-btn">Search</Button>
          </form>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="btn-outline"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />{showFilters ? "Hide" : "Filters"}
            </Button>
            <Link to="/skills-matrix">
              <Button variant="outline" className="btn-outline" data-testid="view-matrix-btn">
                <BarChart3 className="w-4 h-4 mr-2" />Skills Matrix
              </Button>
            </Link>
            {isAdmin && (
              <Select onValueChange={handleExport}>
                <SelectTrigger className="w-auto bg-surface-muted border-slate-200 text-slate-300 gap-2" data-testid="export-btn">
                  <Download className="w-4 h-4" />
                  <span>Export</span>
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="card-surface" data-testid="filters-panel">
            <CardContent className="pt-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Skills</label>
                  <Input
                    value={skillFilter}
                    onChange={e => setSkillFilter(e.target.value)}
                    placeholder="e.g., Quality Assurance"
                    className="input-base"
                    data-testid="filter-skills"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Location</label>
                  <Input
                    value={locationFilter}
                    onChange={e => setLocationFilter(e.target.value)}
                    placeholder="e.g., Chicago, IL"
                    className="input-base"
                    data-testid="filter-location"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Affiliation</label>
                  <Select onValueChange={setAffiliationFilter} value={affiliationFilter}>
                    <SelectTrigger className="input-base" data-testid="filter-affiliation">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all_affiliations">Any</SelectItem>
                      <SelectItem value="current_employee">Current Employee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Profile Type</label>
                  <Select onValueChange={setProfileTypeFilter} value={profileTypeFilter}>
                    <SelectTrigger className="input-base" data-testid="filter-profile-type">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all_types">All Types</SelectItem>
                      {PROFILE_TYPE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" className="btn-outline w-full" onClick={clearFilters} data-testid="clear-filters-btn">
                    <X className="w-4 h-4 mr-2" />Clear All
                  </Button>
                </div>
              </div>

              {/* Quick Skill Tags */}
              {topSkills.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500 mb-2">Popular skills:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {topSkills.slice(0, 10).map(skill => (
                      <button
                        key={skill}
                        onClick={() => { setSkillFilter(skill); setPage(1); }}
                        className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                          skillFilter === skill
                            ? "bg-brand-gold/20 text-brand-navy border-[#FFD507]/40"
                            : "bg-slate-50 text-slate-400 border-slate-200 hover:border-[#FFD507]/30"
                        }`}
                      >
                        {skill}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Results Count */}
        <div className="flex items-center justify-between">
          <p className="text-slate-400 text-sm">
            <span className="text-white font-semibold">{results.total}</span> profiles found
            {(searchQuery || skillFilter || locationFilter || affiliationFilter) && (
              <span className="text-slate-500"> (filtered)</span>
            )}
          </p>
          <p className="text-slate-500 text-sm">
            Page {results.page} of {results.pages || 1}
          </p>
        </div>

        {/* Profile Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="spinner" /></div>
        ) : results.profiles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="talent-grid">
            {results.profiles.map((profile, i) => (
              <TalentProfileCard key={profile.profile_id || i} profile={profile} isAdmin={isAdmin} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-lg">No profiles match your search</p>
            <p className="text-slate-500 text-sm mt-1">Try adjusting your filters or search terms</p>
          </div>
        )}

        {/* Pagination */}
        {results.pages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-4" data-testid="pagination">
            <Button
              variant="outline"
              className="btn-outline"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />Previous
            </Button>
            <span className="text-slate-400 text-sm">
              Page {results.page} of {results.pages}
            </span>
            <Button
              variant="outline"
              className="btn-outline"
              disabled={page >= results.pages}
              onClick={() => setPage(p => p + 1)}
              data-testid="next-page-btn"
            >
              Next<ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default TalentDatabase;
